/**
 * XBRL-based highlighting for SEC filings.
 *
 * iXBRL filings embed XBRL tags inline in the HTML as <ix:nonFraction> elements.
 * This module finds the element matching a given XBRL tag + period end date
 * and highlights it, avoiding the fragile text-search + instance-counting approach.
 */

interface ParsedContext {
  startDate?: string;
  endDate?: string;
  instant?: string;
  hasDimensions: boolean;
}

/**
 * Parse all <context> (or <xbrli:context>) elements in the container and build
 * a lookup map from contextRef ID → parsed period info.
 */
function parseContexts(container: HTMLElement): Map<string, ParsedContext> {
  const contexts = new Map<string, ParsedContext>();

  // Browsers lowercase custom element names; try both cased variants
  const contextElements = [
    ...container.querySelectorAll('context'),
    ...container.querySelectorAll('xbrli\\:context'),
  ];

  for (const ctx of contextElements) {
    const id = ctx.getAttribute('id');
    if (!id) continue;

    const period =
      ctx.querySelector('period') ?? ctx.querySelector('xbrli\\:period');

    let startDate: string | undefined;
    let endDate: string | undefined;
    let instant: string | undefined;

    if (period) {
      const startEl =
        period.querySelector('startDate') ??
        period.querySelector('startdate') ??
        period.querySelector('xbrli\\:startDate') ??
        period.querySelector('xbrli\\:startdate');
      const endEl =
        period.querySelector('endDate') ??
        period.querySelector('enddate') ??
        period.querySelector('xbrli\\:endDate') ??
        period.querySelector('xbrli\\:enddate');
      const instantEl =
        period.querySelector('instant') ??
        period.querySelector('xbrli\\:instant');

      startDate = startEl?.textContent?.trim();
      endDate = endEl?.textContent?.trim();
      instant = instantEl?.textContent?.trim();
    }

    // Check for dimensional qualifiers (segment/scenario with explicitMember)
    const entity =
      ctx.querySelector('entity') ?? ctx.querySelector('xbrli\\:entity');
    const segment =
      entity?.querySelector('segment') ??
      entity?.querySelector('xbrli\\:segment');
    const hasDimensions = segment != null && segment.children.length > 0;

    contexts.set(id, { startDate, endDate, instant, hasDimensions });
  }

  return contexts;
}

/**
 * Find all ix:nonFraction / ix:nonfraction elements matching the given XBRL tag name.
 * Browsers lowercase custom element tag names, so we check both.
 */
function findIxElements(container: HTMLElement, xbrlTag: string): Element[] {
  const results: Element[] = [];

  // Try multiple selector strategies since namespace handling varies
  const selectors = [
    `ix\\:nonfraction[name="${xbrlTag}"]`,
    `ix\\:nonfraction[name="${xbrlTag.toLowerCase()}"]`,
    `ix\\:NONFRACTION[name="${xbrlTag}"]`,
  ];

  for (const selector of selectors) {
    try {
      const elements = container.querySelectorAll(selector);
      for (const el of elements) {
        if (!el.closest('ix\\:hidden')) {
          results.push(el);
        }
      }
    } catch {
      // Invalid selector in some browsers — skip
    }
  }

  // Deduplicate
  return [...new Set(results)];
}

/**
 * Clear any previous XBRL highlights from the container.
 */
export function clearXbrlHighlights(container: HTMLElement): void {
  const highlighted = container.querySelectorAll('.xbrl-highlight');
  for (const el of highlighted) {
    el.classList.remove('xbrl-highlight');
  }
}

/**
 * Find and highlight the ix:nonFraction element matching the given XBRL tag
 * and period. When periodStartDate is provided, matches against the context's
 * startDate to disambiguate quarterly vs YTD facts within the same filing.
 * When omitted (instant metrics or legacy URLs), matches by end date only.
 *
 * @returns true if an element was found and highlighted
 */
export function highlightByXbrlTag(
  container: HTMLElement,
  xbrlTag: string,
  periodEndDate: string,
  periodStartDate?: string | null
): boolean {
  clearXbrlHighlights(container);

  const contexts = parseContexts(container);
  const ixElements = findIxElements(container, xbrlTag);

  if (ixElements.length === 0) return false;

  let bestMatch: Element | null = null;
  let fallbackMatch: Element | null = null;

  for (const el of ixElements) {
    const contextRef =
      el.getAttribute('contextref') ?? el.getAttribute('contextRef');
    if (!contextRef) continue;

    const ctx = contexts.get(contextRef);
    if (!ctx) continue;

    if (ctx.hasDimensions) continue;

    const ctxEndDate = ctx.endDate ?? ctx.instant;
    if (ctxEndDate !== periodEndDate) continue;

    if (periodStartDate) {
      if (ctx.startDate === periodStartDate) {
        bestMatch = el;
        break;
      }
      if (!fallbackMatch) fallbackMatch = el;
    } else {
      bestMatch = el;
      break;
    }
  }

  const match = bestMatch ?? fallbackMatch;
  if (!match) return false;

  match.classList.add('xbrl-highlight');
  match.scrollIntoView({ behavior: 'instant', block: 'center' });
  return true;
}
